/**
 * QorTrace · Qelli — 3D floating Avatar (Iter196 + Iter197).
 *
 * Globally-mounted bottom-right floating launcher. Renders a slowly
 * rotating 3D cyan star using three.js directly (imperative) so we
 * sidestep the CRA / @react-three/fiber `__source` JSX-dev-prop
 * conflict that was blowing up applyProps at runtime.
 *
 * Click behaviour:
 *   • Authed visitor → dispatches `qt-qelli-open` to open the
 *     existing in-product QelliWidget chat panel.
 *   • Un-authed visitor → opens the QelliPreviewModal (sample
 *     Q+A + signup CTA) — same preview surface the floating pill
 *     used to render.
 *
 * Iter197 — proactive "speech bubble" + pulse:
 *   Other surfaces can dispatch a `qt-qelli-pulse` CustomEvent with
 *   `{prompt, intentText, source}` to make Qelli wave at the visitor
 *   with a contextual suggestion ("Want me to explain what this
 *   audit found?"). Click → opens Qelli AND pre-fills the chat input
 *   with the prompt (via `qt-qelli-prefill`) so the visitor only has
 *   to hit Enter. Auto-dismisses after 14 seconds. Capped at one
 *   pulse per sessionStorage key to avoid nagging.
 */
import React, { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { useUserAuth } from "../../lib/userAuth";
import { QelliPreviewModalInline } from "./QelliPublicCTA";
import { trackPulseEvent, rememberPulseSource } from "../../lib/qelliPulseFunnel";
import { QelliFaceAvatar } from "./QelliFaceAvatar";


// ─── Floating avatar (the launcher itself) ─────────────────────────

export default function QelliAvatar3D() {
  const { account } = useUserAuth();
  const isAuthed = !!account;

  const [hovered, setHovered]               = useState(false);
  const [previewOpen, setPreviewOpen]       = useState(false);
  const [chatOpen, setChatOpen]             = useState(false);

  // Iter227 — knowledge-freshness payload (hover tooltip).
  const [freshness, setFreshness] = useState(null);

  // Iter227b/Iter230 — first-visit welcome bubble with A/B copy.
  // Iter232 — Variant selection respects the server's `active` list:
  // once the backend declares a winner (z ≥ 1.96 over runner-up at
  // ≥ 100 impressions each), it returns just one variant and we
  // pin all new visitors to it.
  const [welcomeVisible, setWelcomeVisible] = useState(false);
  const [welcomeVariant, setWelcomeVariant] = useState("control");
  useEffect(() => {
    let cancelled = false;
    try {
      const KEY = "qt_qelli_welcomed_v2";
      const VAR_KEY = "qt_qelli_welcome_variant";
      if (window.localStorage.getItem(KEY) === "1") return;

      const ALL_VARIANTS = ["control", "action", "question"];

      const pickAndShow = (active) => {
        if (cancelled) return;
        // Sticky variant assignment — but if the stored variant is no
        // longer in the active list (winner locked), reassign.
        let v = window.localStorage.getItem(VAR_KEY);
        if (!v || !active.includes(v)) {
          v = active[Math.floor(Math.random() * active.length)] || "control";
          try { window.localStorage.setItem(VAR_KEY, v); } catch {}
        }
        setWelcomeVariant(v);
        window.setTimeout(() => {
          if (cancelled) return;
          setWelcomeVisible(true);
          try {
            trackPulseEvent(`qelli-welcome-${v}`, "fired",
                            window.location.pathname);
          } catch {}
        }, 4000);
      };

      // Ask the server which variants are active. Falls back to
      // serving all three locally if the call fails.
      const base = process.env.REACT_APP_BACKEND_URL || "";
      fetch(`${base}/api/qelli/welcome-active-variants`)
        .then((r) => r.ok ? r.json() : null)
        .then((j) => {
          const active = (j && Array.isArray(j.active) && j.active.length)
            ? j.active.filter((x) => ALL_VARIANTS.includes(x))
            : ALL_VARIANTS;
          pickAndShow(active.length ? active : ALL_VARIANTS);
        })
        .catch(() => pickAndShow(ALL_VARIANTS));
    } catch {}
    return () => { cancelled = true; };
  }, []);
  const dismissWelcome = (clicked = false) => {
    setWelcomeVisible(false);
    try { window.localStorage.setItem("qt_qelli_welcomed_v2", "1"); } catch {}
    if (clicked) {
      try {
        trackPulseEvent(
          `qelli-welcome-${welcomeVariant}`, "click",
          window.location.pathname,
        );
      } catch {}
    }
  };

  // Lazy-load the knowledge-freshness tooltip data on first hover.
  useEffect(() => {
    if (!hovered || freshness !== null) return;
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/public/qelli/knowledge-freshness`)
      .then((r) => r.ok ? r.json() : null)
      .then((j) => { if (j) setFreshness(j); })
      .catch(() => {});
  }, [hovered, freshness]);

  // Pulse / speech-bubble state (Iter197).
  const [pulse, setPulse] = useState(null);
  // Stash the most recent pulse so the preview modal can show the
  // intent even after we've cleared the pulse state on click.
  const [lastIntentText, setLastIntentText] = useState("");
  // pulse shape: { prompt: string, intentText: string, source: string } | null

  // Mirror the in-product chat panel state so the avatar can hide
  // itself when the chat is open (the panel has its own close).
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    setChatOpen(window.sessionStorage.getItem("qt_qelli_open") === "1");
    const sync = () => setChatOpen(
      window.sessionStorage.getItem("qt_qelli_open") === "1",
    );
    const t = setInterval(sync, 400);
    return () => clearInterval(t);
  }, []);

  // Iter276.5 — Shift Qelli UP when the cookie banner is occupying
  // the bottom of the viewport, otherwise the 124px FAB collides
  // with the variable-height banner on mobile (visible bug from
  // the pre-demo readiness sweep). We READ the banner's actual
  // bounding rect so the lift adapts to the banner's wrapped-text
  // height on narrow viewports (it's ~80px on desktop, ~250px on
  // 390px-wide phones).
  const [cookieBannerLift, setCookieBannerLift] = useState(0);
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const probe = () => {
      const el = document.querySelector('[data-testid="cookie-banner"]');
      if (!el) {
        setCookieBannerLift(0);
        return;
      }
      const rect = el.getBoundingClientRect();
      // Lift the FAB so its bottom edge sits 32px above the banner's
      // top edge. The 32px accounts for the FAB's transparent square
      // padding around the circular avatar image — a smaller gap can
      // make the layouts read as touching even though the visible
      // shapes don't actually collide.
      const desiredFromBottom = (window.innerHeight - rect.top) + 32;
      setCookieBannerLift(Math.max(0, desiredFromBottom - 56));
    };
    probe();
    // The banner mounts/unmounts AND can resize on viewport change
    // (orientation flip, text reflow), so we re-probe on both signals.
    const t = setInterval(probe, 400);
    window.addEventListener("resize", probe);
    window.addEventListener("qt:consent-change", probe);
    return () => {
      clearInterval(t);
      window.removeEventListener("resize", probe);
      window.removeEventListener("qt:consent-change", probe);
    };
  }, []);

  // Listen for proactive-pulse events from other surfaces (e.g. verify page).
  // Cap at one pulse per sessionStorage source-key so we don't nag.
  useEffect(() => {
    const onPulse = (e) => {
      const d = e.detail || {};
      const sourceKey = d.source ? `qt_qelli_pulse_seen:${d.source}` : null;
      if (sourceKey && typeof window !== "undefined"
          && window.sessionStorage.getItem(sourceKey) === "1") {
        return;
      }
      if (sourceKey && typeof window !== "undefined") {
        window.sessionStorage.setItem(sourceKey, "1");
      }
      setPulse({
        prompt:     (d.prompt     || "").slice(0, 600),
        intentText: (d.intentText || "Want me to walk you through this?").slice(0, 220),
        source:     d.source || "",
      });
    };
    window.addEventListener("qt-qelli-pulse", onPulse);
    return () => window.removeEventListener("qt-qelli-pulse", onPulse);
  }, []);

  // Auto-dismiss the bubble after 14 s if the visitor ignores it.
  useEffect(() => {
    if (!pulse) return undefined;
    // Iter273.2 — Beta-tester feedback: bubble felt too persistent.
    // Drop auto-dismiss 14s → 8s so it nudges and then gets out of
    // the way (panel itself stays minimizable inside the QelliWidget).
    const t = window.setTimeout(() => setPulse(null), 8000);
    return () => window.clearTimeout(t);
  }, [pulse]);

  // Mount-time gate (preserve existing chatOpen / admin-page hide behavior).
  const visible = !(chatOpen && isAuthed)
    && typeof window !== "undefined"
    && !window.location.pathname.startsWith("/admin");

  if (!visible) return null;

  const openQelli = (prefillPrompt) => {
    // Iter199 — record a funnel click when the visitor engages while
    // a pulse is showing (or in the most-recent pulse's grace window).
    if (pulse?.source) {
      trackPulseEvent(pulse.source, "click");
      rememberPulseSource(pulse.source);
    }
    if (pulse?.intentText) setLastIntentText(pulse.intentText);
    setPulse(null);
    if (isAuthed) {
      window.dispatchEvent(new Event("qt-qelli-open"));
      if (prefillPrompt) {
        // Give the panel one frame to mount before we prefill.
        window.setTimeout(() => {
          window.dispatchEvent(new CustomEvent("qt-qelli-prefill", {
            detail: { text: prefillPrompt },
          }));
        }, 80);
      }
    } else {
      // Stash the intent so the preview modal can show it as context.
      setPreviewOpen(true);
    }
  };

  const onClick = () => openQelli(pulse?.prompt || "");

  return (
    <>
      {/* Pulse ring — radiates outward when grabbing attention. */}
      {pulse && (
        <div
          aria-hidden="true"
          data-testid="qelli-avatar-pulse-ring"
          className="qt-qelli-pulse"
          style={{
            position: "fixed",
            right:    "var(--qt-qelli-pulse-right, -4px)",
            bottom:   "var(--qt-qelli-pulse-bottom, 34px)",
            width:    "var(--qt-qelli-pulse-size, 168px)",
            height:   "var(--qt-qelli-pulse-size, 168px)",
            zIndex:   79,
            borderRadius: "50%",
            border:   "2px solid rgba(0,229,255,0.45)",
            animation: "qelli-pulse-ring 1.8s ease-out infinite",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Speech bubble (Iter197) — anchored above & to the left of the
          orb so it never escapes the viewport on mobile. */}
      {pulse && (
        <div
          data-testid="qelli-avatar-bubble"
          className="qt-qelli-bubble"
          style={{
            position: "fixed",
            right:    22,
            bottom:   "var(--qt-qelli-bubble-bottom, 195px)",
            zIndex:   81,
            width:    "min(320px, calc(100vw - 44px))",
            padding:  "12px 38px 14px 14px",
            background:
              "linear-gradient(135deg, rgba(0,229,255,0.16) 0%, " +
              "rgba(7,12,24,0.96) 70%)",
            border: "1px solid rgba(0,229,255,0.45)",
            borderRadius: 14,
            color: "#fff",
            fontSize: 13,
            lineHeight: 1.45,
            boxShadow: "0 20px 60px rgba(0,229,255,0.18), 0 6px 20px rgba(0,0,0,0.55)",
            backdropFilter: "blur(10px)",
            animation: "qelli-bubble-in 280ms ease-out",
          }}
        >
          <div
            className="qt-mono"
            style={{
              fontSize: 10,
              letterSpacing: "0.14em",
              color: "var(--qt-cyan, #00E5FF)",
              marginBottom: 4,
            }}
          >
            <Sparkles size={10} style={{ display: "inline", marginRight: 4 }} />
            QELLI · QORTRACE'S AI
          </div>
          <div style={{ marginBottom: 10, color: "rgba(255,255,255,0.92)" }}>
            {pulse.intentText}
          </div>
          <button
            type="button"
            data-testid="qelli-avatar-bubble-cta"
            onClick={onClick}
            className="qt-mono"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 12px",
              borderRadius: 999,
              border: "1px solid rgba(0,229,255,0.5)",
              background:
                "linear-gradient(135deg, rgba(0,229,255,0.85) 0%, rgba(108,221,255,0.85) 100%)",
              color: "#0A101D",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.10em",
              cursor: "pointer",
            }}
          >
            <Sparkles size={11} /> ASK QELLI
          </button>

          <button
            type="button"
            aria-label="Dismiss Qelli suggestion"
            data-testid="qelli-avatar-bubble-close"
            onClick={() => setPulse(null)}
            style={{
              position: "absolute",
              top: 8, right: 8,
              background: "transparent",
              border: 0,
              color: "rgba(255,255,255,0.55)",
              cursor: "pointer",
              padding: 4,
              borderRadius: 6,
            }}
          >
            <X size={13} />
          </button>

          {/* Little tail pointing to the orb. */}
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              right: 24,
              bottom: -7,
              width: 14, height: 14,
              background: "rgba(7,12,24,0.96)",
              borderRight: "1px solid rgba(0,229,255,0.45)",
              borderBottom: "1px solid rgba(0,229,255,0.45)",
              transform: "rotate(45deg)",
            }}
          />
        </div>
      )}

      <button
        type="button"
        aria-label={isAuthed ? "Open Qelli — your AI assistant"
                             : "Ask Qelli — free with account"}
        data-testid="qelli-avatar-3d"
        className="qt-qelli-fab"
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          position: "fixed",
          right:    "var(--qt-qelli-right, 18px)",
          // Iter276.5 — Lift over the cookie banner when present.
          // cookieBannerLift is the dynamic offset (px) above the
          // default `bottom: 56px` value, computed from the banner's
          // measured top edge so this adapts to viewport width.
          bottom:   cookieBannerLift > 0
                      ? `calc(var(--qt-qelli-bottom, 56px) + ${cookieBannerLift}px)`
                      : "var(--qt-qelli-bottom, 56px)",
          zIndex:   80,
          // Iter276.8 — Qelli shrunk 20% (124px → 99px). Inline default
          // matches the CSS variable for parity if the stylesheet is
          // ever cache-busted or stripped.
          width:    "var(--qt-qelli-size, 99px)",
          height:   "var(--qt-qelli-size, 99px)",
          padding:  0,
          border:   "0",
          borderRadius: "50%",
          cursor:   "pointer",
          background: "transparent",
          // No ring / no card — Qelli's PNG ships with its own built-in
          // dark-navy circular frame. We just supply the click target.
          boxShadow:  "none",
          transition: "transform 220ms ease, bottom 280ms ease",
          transform:  hovered ? "translateY(-4px) scale(1.05)" : "translateY(0) scale(1)",
          overflow:   "visible",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <QelliFaceAvatar
          size={124}
          hovered={hovered}
          pulsing={!!pulse}
        />

        <span
          aria-hidden="true"
          className="qt-mono"
          data-testid="qelli-hover-tooltip"
          style={{
            position:   "absolute",
            left:       "50%",
            bottom:     -36,
            transform:  "translateX(-50%)",
            fontSize:   10,
            fontWeight: 600,
            letterSpacing: "0.10em",
            color:      "var(--qt-cyan, #00E5FF)",
            background: "rgba(7,12,24,0.96)",
            border:     "1px solid rgba(0,229,255,0.35)",
            borderRadius: 10,
            padding:    "6px 10px",
            whiteSpace: "nowrap",
            textAlign:  "center",
            opacity:    (hovered && !pulse) ? 1 : 0,
            transition: "opacity 180ms ease",
            pointerEvents: "none",
            boxShadow:  "0 6px 18px rgba(0,0,0,0.5)",
            maxWidth:   220,
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Sparkles size={9} /> ASK QELLI
          </span>
          {freshness ? (
            <span
              style={{
                display: "block",
                marginTop: 3,
                fontSize: 9,
                fontWeight: 500,
                color: "var(--qt-text-2, #9CAEC9)",
                letterSpacing: "0.04em",
              }}
            >
              📡 {freshness.stories_24h} stories · 24h
              {freshness.sources_live ? ` · ${freshness.sources_live} sources live` : ""}
            </span>
          ) : null}
        </span>
      </button>

      {/* Iter227b — One-time welcome bubble for first-time visitors. */}
      {welcomeVisible && !pulse && !chatOpen && (
        <div
          data-testid="qelli-welcome-bubble"
          role="dialog"
          aria-label="Qelli welcome"
          style={{
            position: "fixed",
            right:    160,
            bottom:   200,
            zIndex:   2147483646,    /* above cookie banner + everything */
            maxWidth: 312,
            background: "rgba(7,12,24,1)",
            border:   "1px solid rgba(0,229,255,0.45)",
            borderRadius: 14,
            padding:  "14px 16px 14px 18px",
            color:    "#E5EAF1",
            fontSize: 13,
            lineHeight: 1.45,
            boxShadow: "0 18px 48px rgba(0,0,0,0.55), 0 0 24px rgba(0,229,255,0.18)",
            animation: "qelli-bubble-in 320ms ease-out",
          }}
        >
          <button
            type="button"
            onClick={() => dismissWelcome(false)}
            aria-label="Dismiss"
            data-testid="qelli-welcome-dismiss"
            style={{
              position: "absolute",
              top:    6, right: 6,
              width:  22, height: 22,
              borderRadius: "50%",
              border: "0",
              background: "transparent",
              color: "var(--qt-text-3, #7A8AA1)",
              cursor: "pointer",
              fontSize: 14,
              lineHeight: 1,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <X size={13} />
          </button>
          <div
            className="qt-mono"
            data-testid={`qelli-welcome-variant-${welcomeVariant}`}
            style={{
              fontSize: 9.5,
              color: "var(--qt-cyan, #00E5FF)",
              letterSpacing: "0.14em",
              marginBottom: 4,
            }}
          >
            {welcomeVariant === "action"   ? "GET STARTED IN 60s"
             : welcomeVariant === "question" ? "GOT A PQC QUESTION?"
             :                                 "HI — I'M QELLI"}
          </div>
          <div style={{ paddingRight: 18 }}>
            {welcomeVariant === "action" ? (
              <>
                Need to scope a PQC audit, scan a wallet, or
                pick a tier? I'll walk you through it in{" "}
                <strong style={{ color: "#fff" }}>under a minute</strong> —
                with sources.
              </>
            ) : welcomeVariant === "question" ? (
              <>
                Wondering when{" "}
                <strong style={{ color: "#fff" }}>quantum breaks RSA</strong>,
                or which sector got hit this week? Ask — I read every
                cyber + DeFi feed in real time.
              </>
            ) : (
              <>
                Ask me anything about{" "}
                <strong style={{ color: "#fff" }}>QorTrace</strong>,{" "}
                <strong style={{ color: "#fff" }}>post-quantum cryptography</strong>, or
                the latest news in the{" "}
                <strong style={{ color: "#fff" }}>DeFi space</strong>.
              </>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              dismissWelcome(true);
              try {
                window.dispatchEvent(new CustomEvent("qt-qelli-open"));
              } catch {}
            }}
            data-testid="qelli-welcome-cta"
            className="qt-mono"
            style={{
              marginTop: 10,
              padding: "7px 14px",
              borderRadius: 999,
              border: "1px solid var(--qt-cyan, #00E5FF)",
              background: "rgba(0,229,255,0.12)",
              color: "var(--qt-cyan, #00E5FF)",
              cursor: "pointer",
              fontSize: 10,
              letterSpacing: "0.10em",
              fontWeight: 700,
            }}
          >
            {welcomeVariant === "action"   ? "GET STARTED →"
             : welcomeVariant === "question" ? "ASK QELLI →"
             :                                 "START CHATTING →"}
          </button>
          {/* Tail pointing toward Qelli */}
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              right:    -7,
              bottom:   28,
              width:    14, height: 14,
              transform: "rotate(45deg)",
              background: "rgba(7,12,24,1)",
              borderRight: "1px solid rgba(0,229,255,0.45)",
              borderTop:   "1px solid rgba(0,229,255,0.45)",
            }}
          />
        </div>
      )}

      {previewOpen && !isAuthed && (
        <QelliPreviewModalInline
          intentText={lastIntentText}
          onClose={() => { setPreviewOpen(false); setLastIntentText(""); }}
        />
      )}
    </>
  );
}
